import React from "react";

import { ChangeEventTimelineEvent } from "models";

import { ChangeEventDiffTable } from "pages/admin/verifications/components/changeEvents/ChangeEventDiffTable";
import { ChangeEventTypeBadge } from "pages/admin/verifications/components/changeEvents/ChangeEventTypeBadge";
import { deltaDirection, fmtDate, fmtDelta, shortId } from "pages/admin/verifications/utils/changeEventTimeline";

type ChangeEventCauseEffectProps = {
  event: ChangeEventTimelineEvent;
  query?: string;
  onJump: (eventId: string) => void;
};

/**
 * Both directions of the chain. `causationEventId` set with `cause` null is not a bug: the causing row
 * exists but falls outside this field's timeline.
 */
export const ChangeEventCauseEffect = ({ event, query, onJump }: ChangeEventCauseEffectProps) => {
  const { cause, causationEventId, effects } = event;

  return (
    <>
      {causationEventId && !cause && (
        <div className="ce-block">
          <span className="ce-block-label">caused by</span>
          <span className="ce-hint">{`event ${shortId(causationEventId)} — outside this field's timeline`}</span>
        </div>
      )}

      {cause && (
        <div className="ce-block">
          <span className="ce-block-label">caused by</span>
          <div className="ce-cause">
            <ChangeEventTypeBadge type={cause.type} onClick={() => onJump(cause.id)} />
            <time className="ce-mono" dateTime={cause.at} title={fmtDate(cause.at).title}>
              {fmtDate(cause.at).text}
            </time>
            {cause.reason && <span className="ce-hint">{cause.reason}</span>}
          </div>
          <ChangeEventDiffTable diff={cause.diff} truncated={cause.diffTruncated} query={query} label="it changed" />
        </div>
      )}

      {!!effects.length && (
        <div className="ce-block">
          <span className="ce-block-label">caused</span>
          <ul className="ce-effects">
            {effects.map((effect) => (
              <li key={effect.id}>
                <ChangeEventTypeBadge type={effect.type} onClick={() => onJump(effect.id)} />
                <span className={`ce-delta ${deltaDirection(effect.delta)}`}>{fmtDelta(effect.delta)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
};
